// Запуск оффера одной заявкой (meta.launch, правка агента #6) в «Решениях»: что именно создаст платформа — по данным
// заявки, а не по словам агента: своя конверсия, кампания с бюджетом кампании, адсет (возраст, пол, старт), одно
// объявление с роликами, кнопкой и текстом. В журнале — что успело создаться, если Meta отказала на середине.
// Номера роликов пула и видео Meta — под «Показать технически» (данные заявки).
import { plural, usd } from '@autoaff/ui';
import { TriangleAlert } from 'lucide-react';
import type { Proposal } from '../agent-api';

interface LaunchPayload {
  offer?: string;
  pixel_id?: string;
  conversion?: { id?: string; url_contains?: string; fresh?: boolean };
  campaign?: { id?: string; name?: string; objective?: string; daily_usd?: number };
  adset?: { name?: string; age_min?: number; genders?: string; countries?: string[]; start_time?: string };
  ad?: {
    page_id?: string;
    instagram_user_id?: string;
    object_story_id?: string;
    videos?: Array<{ creative_id?: number; video_id?: string }>;
    format?: string;
    link?: string;
    message?: string;
    headline?: string;
    cta?: string;
  };
  activate?: boolean;
}

/** Кнопки Meta так, как их видит покупатель. */
const CTA: Record<string, string> = {
  ORDER_NOW: 'Заказать',
  LEARN_MORE: 'Подробнее',
  SHOP_NOW: 'В магазин',
  BUY_NOW: 'Купить',
  SIGN_UP: 'Зарегистрироваться',
  CONTACT_US: 'Связаться с нами',
  GET_OFFER: 'Получить предложение',
  SEE_DETAILS: 'Подробнее',
};

const GENDER: Record<string, string> = { all: 'все', male: 'мужчины', female: 'женщины' };

const payloadOf = (p: Pick<Proposal, 'payload'>) => (p.payload ?? {}) as LaunchPayload;

/** Ролики объявления и в каком формате — по-человечески. */
function videosText(o: LaunchPayload): string {
  const ad = o.ad ?? {};
  if (ad.object_story_id) return 'существующий пост страницы';
  const vs = ad.videos ?? [];
  const n = vs.length;
  const objective = o.campaign?.objective ?? (o.campaign?.id ? null : 'OUTCOME_LEADS');
  const format = ad.format ?? (n <= 1 ? 'single' : objective === 'OUTCOME_SALES' ? 'flexible' : 'dynamic');
  const pool = vs.filter((v) => v.creative_id).length;
  const meta = n - pool;
  const from = [
    pool ? `${pool} из пула` : null,
    meta ? `${meta} ${plural(meta, 'уже загружен', 'уже загружены', 'уже загружены')} в Meta` : null,
  ]
    .filter(Boolean)
    .join(', ');
  const kind =
    format === 'dynamic' ? ' · динамическое оформление' : format === 'flexible' ? ' · гибкий формат' : '';
  return `${n} ${plural(n, 'ролик', 'ролика', 'роликов')} (${from})${kind}`;
}

function startText(s: string | undefined): string {
  if (!s || s === 'midnight') return 'ближайшие 00:00 по Ташкенту';
  if (s === 'now') return 'сразу после одобрения';
  const t = Date.parse(s);
  if (!Number.isFinite(t)) return s;
  const d = new Date(t + 5 * 3_600_000).toISOString();
  return `${d.slice(8, 10)}.${d.slice(5, 7)} ${d.slice(11, 16)} по Ташкенту`;
}

/** Что сделает платформа после одобрения — одной строкой. */
export function launchLine(p: Pick<Proposal, 'payload'>): string {
  const o = payloadOf(p);
  const c = o.campaign ?? {};
  const camp = c.id
    ? 'в вашей кампании с бюджетом кампании'
    : `«${c.name ?? ''}», ${c.objective === 'OUTCOME_SALES' ? 'продажи' : 'лиды'}, ${c.daily_usd !== undefined ? usd(c.daily_usd) : '—'} в день на кампании`;
  return `Запуск оффера ${o.offer ?? ''}: своя конверсия → кампания ${camp} → адсет → одно объявление`;
}

/** Строки карточки: из чего состоит запуск (внутри списка фактов карточки). */
export function LaunchPlan({ p }: { p: Proposal }) {
  const o = payloadOf(p);
  const ad = o.ad ?? {};
  const a = o.adset ?? {};
  const conv = o.conversion ?? {};
  const host = (() => {
    try {
      return ad.link ? new URL(ad.link).hostname : null;
    } catch {
      return null;
    }
  })();
  const rule = conv.url_contains ?? (host && o.offer ? `${host}/market/${o.offer}/` : '—');
  const appDev = (p.policy as { app_dev?: boolean } | null)?.app_dev === true;
  return (
    <>
      <div>
        <dt>Конверсия</dt>
        <dd>
          {conv.id
            ? 'готовая, указанная агентом'
            : conv.fresh
              ? `новая: событие Lead, адрес содержит «${rule}»`
              : `своя для оффера: событие Lead, адрес содержит «${rule}» — возьмём готовую с таким правилом или создадим`}
        </dd>
      </div>
      <div>
        <dt>Адсет</dt>
        <dd>
          {(a.countries ?? ['UZ']).join(', ')} · {a.age_min ?? 18}–65 ·{' '}
          {GENDER[a.genders ?? 'all'] ?? a.genders} · Advantage+ аудитория
          <small>старт: {startText(a.start_time)}</small>
        </dd>
      </div>
      <div>
        <dt>Объявление</dt>
        <dd>
          {videosText(o)}
          {!ad.object_story_id && (
            <small>
              кнопка «{CTA[ad.cta ?? 'LEARN_MORE'] ?? ad.cta}» · {ad.link ?? '—'}
            </small>
          )}
        </dd>
      </div>
      {ad.message && (
        <div>
          <dt>Текст</dt>
          <dd className="prop-text">
            {ad.headline && <b>{ad.headline}</b>}
            {ad.message}
          </dd>
        </div>
      )}
      <div>
        <dt>Как выполнит</dt>
        <dd>
          Всё создаётся на паузе и включается одним шагом в конце
          {o.activate === false ? ' — здесь не включается, включите сами' : ''}. Если Meta откажет на
          середине, созданное останется на паузе: покажем, что создано, и дадим довести запуск или остановить
          созданное.
          {appDev && (
            <span className="notice warning prop-notice">
              <TriangleAlert />
              Meta в последний раз ответила, что приложение платформы в режиме разработки — новое объявление
              не создастся. Переведите приложение в «Опубликовано»: developers.facebook.com → «Мои приложения»
              → приложение платформы → переключатель «Режим приложения».
            </span>
          )}
        </dd>
      </div>
    </>
  );
}

const STEP: Array<[keyof Launched, string]> = [
  ['conversion', 'конверсия'],
  ['campaign', 'кампания'],
  ['adset', 'адсет'],
  ['ad', 'объявление'],
];

export interface Launched {
  conversion?: { id?: string; created?: boolean };
  campaign?: string;
  adset?: string;
  ad?: string;
  active?: boolean;
}

/** Что успел создать недоведённый запуск: «Создано (на паузе): … · не создано: …». */
export function launchProgress(created: Launched | null | undefined, ownCampaign: boolean): string | null {
  if (!created) return null;
  const steps = STEP.filter(([k]) => !(ownCampaign && k === 'campaign'));
  const made = steps.flatMap(([k, t]) => (created[k] ? [t] : []));
  const left = steps.filter(([k]) => !created[k]).map(([, t]) => t);
  if (!made.length) return 'В Meta ничего не создано';
  return `Создано (на паузе): ${made.join(', ')}${left.length ? ` · не создано: ${left.join(', ')}` : ''}`;
}
